/**
 * The ticket number out of a deep link, as the backend's by-number lookup wants it.
 *
 * The lookup is an exact match on Ivanti's number field, so this only removes
 * what a person or a mail client adds around a number — surrounding
 * whitespace and a leading `#` ("#12345") — and passes the rest through
 * untouched. Import-free so `tests/ticket-number.test.mjs` can load it directly.
 */
export function normaliseTicketNumber(raw: string | null | undefined): string {
  if (typeof raw !== "string") {
    return "";
  }
  return raw.trim().replace(/^#+/, "").trim();
}

/** Answers that mean "no such ticket for you", as opposed to "we could not ask". */
export const NOT_FOUND_CODES: ReadonlySet<string> = new Set([
  "TICKET_NOT_FOUND",
  "TICKET_NOT_YOURS",
  "NOT_FOUND",
]);

/**
 * True when a failed ticket read means "there is no such ticket for you".
 *
 * A ticket that is not theirs (403) and one that is not anyone's (404) read
 * the same way, because telling a stranger that a ticket exists is already
 * more than they should learn. Takes the normalised `{ status, code }` so it
 * stays import-free.
 */
export function isTicketNotFound(status: number | null, code: string | null): boolean {
  return status === 404 || status === 403 || (code !== null && NOT_FOUND_CODES.has(code));
}
